import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useState } from 'react';

type Size = { label: string; qty: number | null };
type Product = {
  code: string;
  name: string;
  image: string;
  sizes: Size[];
  /** 2. jakost — pozná se podle názvu, řadí se na konec a má jiné podbarvení. */
  second_quality?: boolean;
};
type Data = { latka: string; products: Product[]; disabled?: boolean };

/**
 * Produkty ušité ze stejné látky + jejich dostupnost v e-shopu. Střihárna
 * podle toho vidí, co všechno se z role dá nastřihat a co zrovna chybí na
 * prodejně. Množství je z e-shopu (shoptet_variant_stocks), ne z našeho skladu.
 */
export function LatkaProduktyModal({
  productCode,
  onClose,
}: {
  productCode: string;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<Data>(`/api/vyroba/striharna/latka-produkty?code=${encodeURIComponent(productCode)}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : 'Nepodařilo se načíst produkty');
      });
    return () => {
      cancelled = true;
    };
  }, [productCode]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-5xl my-8 bg-surface rounded-2xl shadow-modal reveal">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-border">
          <h2 className="text-lg font-bold text-text">
            Produkty z látky{data?.latka ? `: ${data.latka}` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-text text-2xl leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {err && <p className="text-sm text-danger">{err}</p>}
          {!err && data === null && <p className="text-sm text-text-muted">Načítám…</p>}
          {data?.products.length === 0 &&
            (data.disabled ? (
              <p className="text-sm text-text-muted">
                Produkt má v databázi produktů místo látky zástupku „DISABLED", takže není z čeho
                vycházet. Přiřaď mu skutečnou látku a detail začne fungovat.
              </p>
            ) : (
              <p className="text-sm text-text-muted">
                K tomuto produktu není navázaná látka, nebo z ní není nic dalšího.
              </p>
            ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(data?.products ?? []).map((p) => (
              <div
                key={p.code}
                className={`rounded-xl p-4 border ${
                  p.second_quality ? 'border-warning/40 bg-warning-light' : 'border-border'
                }`}
              >
                <div className="flex gap-4">
                  {p.image ? (
                    <img
                      src={p.image}
                      alt=""
                      loading="lazy"
                      className="w-24 h-24 shrink-0 object-contain rounded-lg border border-border bg-white p-1"
                    />
                  ) : (
                    <div className="w-24 h-24 shrink-0 rounded-lg border border-border bg-bg" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-text text-center">{p.code}</div>
                    <div className="text-sm text-text-secondary text-center">{p.name}</div>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  {p.sizes.map((s) => (
                    <div
                      key={s.label}
                      className={`flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm ${
                        p.second_quality ? 'bg-surface' : 'bg-bg'
                      }`}
                    >
                      <span className="text-text">{s.label}</span>
                      {s.qty === null ? (
                        <span className="text-text-muted" title="Variantu se nepodařilo dohledat">
                          stav neznámý
                        </span>
                      ) : s.qty > 0 ? (
                        <span className="font-semibold text-action-dark">Skladem: {s.qty} ks</span>
                      ) : (
                        <span className="font-semibold text-danger">Není skladem</span>
                      )}
                    </div>
                  ))}
                  {p.sizes.length === 0 && (
                    <p className="text-xs text-text-muted">Kategorie nemá vyplněné velikosti.</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {(data?.products.length ?? 0) > 0 && (
            <p className="mt-5 text-xs text-text-muted">
              Skladovost je z e-shopu, ne z našeho skladu — aktualizuje se každých 15 minut z
              exportu Shoptetu „TOOLS a VÝROBA - Skladové zásoby".
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
